/**
 * The "Sync to Thrifty" bookmarklet and the receiving end of its handoff.
 *
 * Clicked on a marketplace shop or sold page (Depop, Vinted, eBay, Poshmark),
 * it opens a small panel and starts recording the JSON the page itself loads:
 * it wraps fetch and XMLHttpRequest, keeps same-site JSON responses, and can
 * scroll the page to the end so every page of results loads. "Send" opens
 * Thrifty's /sync page and hands the recording over with postMessage — those
 * sites' CSP blocks a cross-origin request, and this way the bookmark carries
 * no credentials. "Save file" is the fallback for a site whose
 * Cross-Origin-Opener-Policy cuts the popup off; the file is uploaded on /sync.
 *
 * It knows nothing about any site's data shapes: parsing is done server-side
 * (services/api/src/lib/marketSync.ts), so a site changing its API is fixed
 * there without anyone re-installing the bookmark. Bump VERSION only when
 * this collector itself changes.
 */

export const BOOKMARKLET_VERSION = 3;

/**
 * Runs on the marketplace page. Serialised with Function.prototype.toString,
 * so it must be self-contained: no imports, no outside references, and DOM
 * built with textContent only (some sites enforce Trusted Types).
 */
function collector(A: string, V: number) {
  const w = window as any;
  if (w.__thriftySync) { w.__thriftySync.panel.style.display = "block"; return; }

  const CAP_TOTAL = 12_000_000;
  const CAP_ONE = 2_500_000;
  const HTML_CAP = 1_500_000;
  const base = location.hostname.split(".").slice(-2).join(".");
  const caps: Array<{ url: string; body: unknown }> = [];
  const state: any = { panel: null, reachedEnd: false };
  w.__thriftySync = state;
  let bytes = 0;

  const panel = document.createElement("div");
  state.panel = panel;
  panel.setAttribute("style", [
    "position:fixed", "right:16px", "bottom:16px", "z-index:2147483647", "width:280px",
    "background:#1f2a24", "color:#f4f1ea", "font:13px/1.4 system-ui,sans-serif",
    "border-radius:10px", "padding:12px 14px", "box-shadow:0 6px 24px rgba(0,0,0,.35)",
  ].join(";"));
  const title = document.createElement("div");
  title.textContent = "Sync to Thrifty";
  title.setAttribute("style", "font-weight:700;margin-bottom:4px");
  const count = document.createElement("div");
  const status = document.createElement("div");
  status.setAttribute("style", "opacity:.8;margin:4px 0 8px;min-height:1.4em");
  const row = document.createElement("div");
  row.setAttribute("style", "display:flex;gap:6px;flex-wrap:wrap");
  const button = (label: string, onClick: () => void) => {
    const b = document.createElement("button");
    b.textContent = label;
    b.setAttribute("style", "background:#f4f1ea;color:#1f2a24;border:0;border-radius:6px;padding:5px 9px;font:600 12px system-ui,sans-serif;cursor:pointer");
    b.addEventListener("click", onClick);
    row.appendChild(b);
    return b;
  };
  const say = (t: string) => { status.textContent = t; };
  const paint = () => {
    count.textContent = `${caps.length} response${caps.length === 1 ? "" : "s"} recorded (${Math.round(bytes / 1024)} KB)`;
  };
  panel.append(title, count, status, row);
  document.body.appendChild(panel);

  const seenUrls = new Set<string>();
  const onSite = (url: string) => {
    try {
      const h = new URL(url, location.href).hostname;
      return h === base || h.endsWith("." + base);
    } catch { return false; }
  };

  const keep = (url: string, text: unknown) => {
    if (typeof text !== "string" || text.length < 2 || text.length > CAP_ONE) return;
    if (seenUrls.has(String(url))) return;
    if (bytes + text.length > CAP_TOTAL) { say("Recording is full — send what you have."); return; }
    const c = text.trimStart().charAt(0);
    if (c !== "{" && c !== "[") return;
    if (!onSite(url)) return;
    let body: unknown;
    try { body = JSON.parse(text); } catch { return; }
    seenUrls.add(String(url));
    caps.push({ url: String(url), body });
    bytes += text.length;
    paint();
  };

  // What the page already rendered before we were clicked.
  document
    .querySelectorAll('script[type="application/json"],script[type="application/ld+json"]')
    .forEach((s, i) => keep(`${location.href}#script-${(s as HTMLElement).id || i}`, s.textContent));

  // Server-rendered state. Poshmark puts the first page of a closet in
  // window.__INITIAL_STATE__ near the end of a ~1.7 MB page, past the HTML cap.
  ["__INITIAL_STATE__", "__NEXT_DATA__", "__NUXT__", "__APOLLO_STATE__", "__PRELOADED_STATE__"].forEach((k) => {
    try {
      const v = w[k];
      // An element with that id is also a window property; skip DOM nodes.
      if (v && typeof v === "object" && !(v instanceof Node)) keep(`${location.href}#${k}`, JSON.stringify(v));
    } catch { /* circular or unserialisable; skip */ }
  });

  // Headers the site's own API calls carry (CSRF or bearer tokens), per host,
  // so data the page loaded before we were clicked can be re-requested exactly
  // as the page asked for it.
  const learned: Record<string, Record<string, string>> = {};
  const learn = (url: string, name: string, value: string) => {
    if (!/^(authorization|x-|locale$)/i.test(name) || /^x-datadog/i.test(name)) return;
    try {
      const h = new URL(url, location.href).hostname;
      (learned[h] = learned[h] || {})[name] = value;
    } catch { /* ignore */ }
  };
  let inFlight = 0;

  const origFetch = w.fetch;
  if (origFetch) {
    w.fetch = function (this: unknown, ...args: any[]) {
      try {
        const url = typeof args[0] === "string" ? args[0] : args[0]?.url ?? String(args[0]);
        if (onSite(url)) {
          new Headers(args[1]?.headers ?? (typeof args[0] === "object" ? args[0]?.headers : undefined))
            .forEach((v, k) => learn(url, k, v));
        }
      } catch { /* never break the page */ }
      inFlight += 1;
      const p = origFetch.apply(this, args);
      p.then((r: Response) => {
        inFlight -= 1;
        try {
          if (/json/i.test(r.headers.get("content-type") || "")) {
            r.clone().text().then((t) => keep(r.url, t), () => {});
          }
        } catch { /* never break the page */ }
      }, () => { inFlight -= 1; });
      return p;
    };
  }
  const XO = XMLHttpRequest.prototype.open;
  const XS = XMLHttpRequest.prototype.send;
  const XH = XMLHttpRequest.prototype.setRequestHeader;
  XMLHttpRequest.prototype.open = function (this: any, ...args: any[]) {
    this.__thriftyUrl = args[1];
    return (XO as any).apply(this, args);
  } as any;
  XMLHttpRequest.prototype.setRequestHeader = function (this: any, name: string, value: string) {
    try { if (onSite(this.__thriftyUrl)) learn(this.__thriftyUrl, name, value); } catch { /* ignore */ }
    return XH.call(this, name, value);
  };
  XMLHttpRequest.prototype.send = function (this: any, ...args: any[]) {
    const x = this;
    inFlight += 1;
    x.addEventListener("loadend", () => {
      inFlight -= 1;
      try {
        const u = x.responseURL || x.__thriftyUrl;
        if (x.responseType === "" || x.responseType === "text") keep(u, x.responseText);
        else if (x.responseType === "json" && x.response) keep(u, JSON.stringify(x.response));
      } catch { /* never break the page */ }
    });
    return (XS as any).apply(this, args);
  } as any;

  // Everything the page fetched before the click is in the resource timing
  // log. Re-request each same-site data URL with the page's cookies and the
  // learned headers. The log has no method, so anything that looks like
  // telemetry (usually a POST) is skipped.
  try { performance.setResourceTimingBufferSize(10000); } catch { /* ignore */ }
  const NOISE = /event|relay|track|analytic|metric|collect|logging|experiment|feature|banner|beacon|pixel|session|counters|config|consent|captcha/i;
  const replay = async () => {
    const urls = (performance.getEntriesByType("resource") as PerformanceResourceTiming[])
      .filter((e) => (e.initiatorType === "fetch" || e.initiatorType === "xmlhttprequest") && onSite(e.name))
      .map((e) => e.name)
      .filter((u, i, all) => all.indexOf(u) === i && !seenUrls.has(u) && !NOISE.test(u))
      .slice(0, 80);
    for (const u of urls) {
      const target = new URL(u);
      const headers = learned[target.hostname] || {};
      // Cross-origin APIs (webapi.depop.com) answer `*` and refuse cookies;
      // try the page's likely mode first, then the other one.
      const modes: RequestCredentials[] = target.origin === location.origin ? ["include"] : ["omit", "include"];
      for (const credentials of modes) {
        try {
          const r: Response = await origFetch.call(window, u, { credentials, headers });
          if (r.ok && /json/i.test(r.headers.get("content-type") || "")) { keep(u, await r.text()); break; }
          if (r.status !== 401 && r.status !== 403) break;
        } catch { /* CORS refusal: try the next mode */ }
      }
    }
  };

  // Scroll until nothing new arrives. Clicks an obvious "Load more" button when
  // a site uses one instead of infinite scroll.
  let scrolling: number | null = null;
  const scrollAll = () => {
    if (scrolling) { clearInterval(scrolling); scrolling = null; say("Stopped."); return; }
    let still = 0, ticks = 0, lastH = 0, lastN = caps.length;
    say("Loading everything… keep this tab in front.");
    scrolling = window.setInterval(() => {
      ticks += 1;
      const more = Array.from(document.querySelectorAll("button")).find(
        (b) => /^\s*(load|show|see|view) more\b/i.test(b.textContent || "") && (b as HTMLElement).offsetParent
      );
      if (more) (more as HTMLElement).click();
      const links = document.querySelectorAll("a[href]");
      if (links.length) links[links.length - 1].scrollIntoView({ block: "end" });
      window.scrollTo(0, document.documentElement.scrollHeight);
      // Selling dashboards often scroll an inner panel rather than the window.
      let h = document.documentElement.scrollHeight;
      document.querySelectorAll("main, section, div, ul").forEach((el) => {
        const e = el as HTMLElement;
        if (e.scrollHeight > e.clientHeight + 100 && /(auto|scroll)/.test(getComputedStyle(e).overflowY)) {
          e.scrollTop = e.scrollHeight;
          h += e.scrollHeight;
        }
      });
      // Only "still" once nothing is loading, nothing grew and nothing arrived.
      still = h === lastH && caps.length === lastN && inFlight <= 0 ? still + 1 : 0;
      lastH = h;
      lastN = caps.length;
      if (still >= 8 || ticks >= 500) {
        clearInterval(scrolling!);
        scrolling = null;
        state.reachedEnd = still >= 8;
        say(state.reachedEnd ? "Reached the end. Press Send." : "Stopped after 500 steps; send what loaded.");
      }
    }, 1500);
  };

  const payload = () => ({
    type: "thrifty-sync",
    version: V,
    pageUrl: location.href,
    pageTitle: document.title,
    host: location.hostname,
    reachedEnd: state.reachedEnd,
    captures: caps,
    html: document.documentElement.outerHTML.slice(0, HTML_CAP),
  });

  const send = async () => {
    // Open first, while the click still counts as a user gesture.
    const popup = window.open(A + "/sync", "thrifty-sync");
    if (!popup) { say("Allow pop-ups for this site, then press Send again."); return; }
    say("Collecting…");
    await replay();
    const msg = payload();
    let acked = false;
    const post = () => { try { popup.postMessage(msg, A); } catch { /* not loaded yet */ } };
    const onMsg = (e: MessageEvent) => {
      if (e.origin !== A) return;
      if (e.data === "thrifty-sync-ready") post();
      if (e.data === "thrifty-sync-ack") {
        acked = true;
        clearInterval(t);
        window.removeEventListener("message", onMsg);
        say("Sent. Review it in the Thrifty window.");
      }
    };
    window.addEventListener("message", onMsg);
    const t = window.setInterval(post, 2000);
    setTimeout(() => {
      if (acked) return;
      clearInterval(t);
      window.removeEventListener("message", onMsg);
      say("Thrifty didn't answer. Use Save file and upload it on Thrifty's Sync page.");
    }, 60_000);
  };

  const save = async () => {
    say("Collecting…");
    await replay();
    const blob = new Blob([JSON.stringify(payload())], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `thrifty-sync-${base.split(".")[0]}-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    say("Saved. Upload it on Thrifty's Sync page.");
  };

  button("Load everything", scrollAll);
  button("Send", () => { void send(); });
  button("Save file", () => { void save(); });
  button("×", () => { panel.style.display = "none"; });
  paint();
  say("Collecting what the page already loaded…");
  // A moment first, so the page's own traffic can teach us its headers.
  setTimeout(() => {
    // Page 1 loaded before the click and some sites refuse a replay, so the
    // dependable move is to make the page load its list again while we listen.
    void replay().then(() => say("Recording. Now switch the page's tab or filter away and back (e.g. Sold, then Active) so the list reloads, then press Load everything."));
  }, 1500);
}

export function bookmarkletHref(appOrigin: string): string {
  const js = `(${collector.toString()})(${JSON.stringify(appOrigin)},${BOOKMARKLET_VERSION})`;
  return "javascript:" + encodeURIComponent(js);
}

/* --- Receiving side -------------------------------------------------------- */

export interface SyncCapturePayload {
  version: number;
  pageUrl: string;
  pageTitle: string;
  host: string;
  reachedEnd: boolean;
  captures: Array<{ url: string; body: unknown }>;
  html?: string;
}

const ALLOWED = /(^|\.)(depop|vinted|ebay|poshmark)\.com$/i;
const EVENT = "thrifty-sync-pending";

// Held in memory: a full shop's recording can outgrow sessionStorage's quota,
// and signing in inside the popup does not reload the page.
let pending: SyncCapturePayload | null = null;

export function isSyncPayload(d: unknown): d is SyncCapturePayload & { type?: string } {
  const x = d as Partial<SyncCapturePayload> | null;
  return !!x && typeof x.host === "string" && typeof x.pageUrl === "string" && Array.isArray(x.captures);
}

/**
 * Registered at startup, before sign-in, so the handoff is caught and acked
 * even while the popup is showing the sign-in form.
 */
export function listenForSync() {
  window.addEventListener("message", (e: MessageEvent) => {
    let host = "";
    try { host = new URL(e.origin).hostname; } catch { return; }
    if (!ALLOWED.test(host)) return;
    const d = e.data as { type?: string };
    if (d?.type !== "thrifty-sync" || !isSyncPayload(d)) return;
    (e.source as Window | null)?.postMessage("thrifty-sync-ack", e.origin);
    // The bookmarklet re-posts until it hears the ack; take the first copy.
    if (pending && pending.pageUrl === d.pageUrl && pending.captures.length === d.captures.length) return;
    pending = d;
    window.dispatchEvent(new Event(EVENT));
  });

  // Tell the opener we are listening, so it posts once instead of waiting for
  // its retry timer. The opener's origin is unknown here, and the message
  // carries nothing.
  if (window.opener && window.location.pathname === "/sync") {
    try { (window.opener as Window).postMessage("thrifty-sync-ready", "*"); } catch { /* severed */ }
  }
}

export function takePending(): SyncCapturePayload | null {
  const p = pending;
  pending = null;
  return p;
}

export function onPending(fn: () => void): () => void {
  window.addEventListener(EVENT, fn);
  return () => window.removeEventListener(EVENT, fn);
}
